import React, { useEffect, useState, Suspense, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardHeader,
  Button,
  Container,
  Modal,
  IconButton
} from "@mui/material";
import {
  Edit as EditIcon,
  Close,
  Save as SaveIcon,
  Save
} from "@mui/icons-material";
import RichTextEditor from "./RichTextEditor";
import { selectUsersTemplates } from "../../features/users/userSelectors";
import { fetchAllTemplates } from "../../features/users/userSlice";
import { updateTemplate } from "../../api/userApi";
import { deleteFile } from "../../utils/deleteFile";
import { dataURLtoFile } from "../../utils/dataURLtoFile";
import { uploadFile } from "../../utils/uploadFile";
import { generateSignedUrl } from "../../utils/getPreSignedURL";
import useSanitizeContent from "../../utils/useSanitizeContent";
import useThemeColor from "../../utils/useTheme";
import "../Tabs/AdminPortal.css"; // Assuming you have this CSS file
import { sanitizeContent } from "../../utils/sanitizeContent";
import SendgridStatsDashboard from "./SendgridStats";

const isValidUrl = (url) => {
  return /^(https?:\/\/|www\.)/.test(url);
};

const AutomatedEmails = ({ isAdmin, userId }) => {
  const templates = useSelector(selectUsersTemplates);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [openFAQModal, setOpenFAQModal] = useState(false);
  const [value, setValue] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const styles = useThemeColor();

  // Template name mapping
  const templateNames = {
    1: "#1 Registration Confirmation (sent immediately)",
    2: "#2 Logging info (sent 24 hours after #1)",
    3: "#3 MOD info (sent 24 hours after #2)",
    4: "#4 Badges info (sent 24 hours after #3)",
    5: "#5 Free Trial Reminder on Day 5 after registration (sent 48 hours after #4)",
    6: "#6 Free Trial Reminder Last day (sent 10 days after #1)"
  };

  useEffect(() => {
    setLoading(true);
    dispatch(fetchAllTemplates());
    setLoading(false);
  }, [dispatch]);

  const updateImageSources = useCallback(async (html) => {
    if (!html) return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    // Process images
    const images = Array.from(doc.querySelectorAll("img"));
    for (let img of images) {
      const fileName = img.getAttribute("src");

      if (fileName && !fileName.startsWith("http")) {
        try {
          const signedUrl = await generateSignedUrl(
            fileName,
            process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
          );
          img.src = signedUrl;
        } catch (error) {
          console.error("Error generating signed URL:", error);
        }
      }

      if (!img.hasAttribute("width")) img.setAttribute("width", "100%");
      if (!img.hasAttribute("height")) img.setAttribute("height", "auto");
    }

    // Process links
    const links = Array.from(doc.querySelectorAll("a"));
    links.forEach((link) => {
      link.target = "_blank";
      const href = link.getAttribute("href");
      if (href) {
        if (href.startsWith("www.")) {
          link.setAttribute("href", `https://${href}`);
        } else if (!isValidUrl(href)) {
          link.setAttribute("href", `https://${href}`);
        }
      }
      // Do NOT clear inline styles
    });

    // Do NOT reset styles for these elements unless you want to override with theme color
    // const styledElements = [...doc.querySelectorAll("span, strong, h1, h2, h3, h4, h5, h6, p")];
    // styledElements.forEach(el => {
    //   if (!el.style.color) el.style.color = styles.color; // optional if applying theme
    // });

    return doc.body.innerHTML;
  }, []);

  useEffect(() => {
    if (templates && templates.length > 0) {
      // Process all templates to add signed URLs for images
      const processTemplates = async () => {
        const processed = [];
        for (const template of templates) {
          const processedContent = await updateImageSources(
            template.content,
            template
          );
          processed.push({
            ...template,
            processedContent
          });
        }
        if (userId) {
          processed.splice(processed.length - 2, 2);
        }
        setEmailTemplates(processed);
      };

      processTemplates();
    }
  }, [templates, loading, updateImageSources]);

  const handleSubjectChange = (template, e) => {
    const updatedTemplates = emailTemplates.map((item) => {
      if (item._id === template._id) {
        return { ...item, subject: e.target.value };
      }
      return item;
    });
    setEmailTemplates(updatedTemplates);
  };

  const handleActiveToggle = async (template) => {
    if (isAdmin) {
      await updateTemplate({
        active: !template.active,
        _id: template._id
      });
    } else {
      await updateTemplate({
        excluded_user: userId,
        _id: template._id
      });
    }
    dispatch(fetchAllTemplates());
  };

  const getTemplateName = (sequence) => {
    return templateNames[sequence] || `Email Template ${sequence}`;
  };

  return (
    <Container maxWidth="xl" sx={{ m: 0, p: 0, maxWidth: "100%" }}>
      {isAdmin ? <SendgridStatsDashboard /> : <></>}
      <Box sx={{ mb: 4, mt: isAdmin ? 3 : 0 }}>
        <Typography variant="body1" color="text.secondary">
          These emails are to help onboard new users. Everyone who registers
          through your link gets an registration confirmation. They will get the
          remaining sequence unless you toggle them off.
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {emailTemplates?.length ? (
          emailTemplates?.map((template) => (
            <Grid item xs={12} md={6} key={template._id}>
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s",
                  "&:hover": {
                    boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)"
                  }
                }}
              >
                <CardHeader
                  title={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EditIcon sx={{ mr: 1, color: "#1976d2" }} />
                      <Typography variant="body1">
                        {getTemplateName(template.sequence)}
                      </Typography>
                    </Box>
                  }
                  action={
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={template.sequence === 1}
                          checked={
                            userId
                              ? !template.excluded_users.includes(userId)
                              : template.active
                          }
                          onChange={() => handleActiveToggle(template)}
                          color="primary"
                        />
                      }
                      label={template.active ? "Active" : "Inactive"}
                    />
                  }
                  sx={{
                    backgroundColor: template.active
                      ? "rgba(25, 118, 210, 0.08)"
                      : "inherit",
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                  }}
                />
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    ".quill": {
                      padding: "0 !important"
                    }
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <TextField
                      label="Subject"
                      fullWidth
                      variant="outlined"
                      disabled={!isAdmin || template.sequence === 1}
                      value={template.subject}
                      onChange={(e) => handleSubjectChange(template, e)}
                      sx={{ mb: 3 }}
                      InputProps={{
                        sx: { borderRadius: 1 }
                      }}
                    />
                    {!isAdmin ? (
                      <></>
                    ) : (
                      <IconButton
                        color="primary"
                        sx={{
                          // svg: {
                          //   fill: "#1c1c1c"
                          // },
                          mb: 3
                        }}
                        disabled={template.sequence === 1}
                        onClick={async () => {
                          await updateTemplate({
                            subject: template.subject,
                            _id: template._id
                          });
                        }}
                      >
                        <Save />
                      </IconButton>
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ color: "#666" }}
                  >
                    Email Content
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      m: 0,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      mb: 2,
                      minHeight: "100px",
                      maxHeight: "400px",
                      overflow: "auto"
                    }}
                    className="preview-html"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeContent(
                        template.processedContent || template.content
                      )
                    }}
                  />

                  {/* <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<EditIcon />}
                      onClick={() => openModal(template)}
                    >
                      Edit Template
                    </Button>
                  </Box> */}
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1">No templates found</Typography>
          </Box>
        )}
      </Grid>
      {/* Email Template Edit Modal */}
      {/* <Modal
        open={openFAQModal}
        onClose={handleCloseModal}
        aria-labelledby="email-template-modal-title"
        aria-describedby="email-template-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "7%",
            left: "10%",
            overflow: "auto",
            height: { xs: "80%", md: "90%" },
            width: { md: "80%", xs: "100%" },
            borderRadius: 2
          }}
        >
          <Box
            sx={{
              padding: { xs: "28px", md: "10px" },
              position: "relative",
              bgcolor: "#FCFCFC",
              borderRadius: "16px 16px 0 0",
              boxShadow: 24,
              overflow: "hidden"
            }}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              px={3}
              pt={1}
            >
              <Typography variant="h6">
                {selectedTemplate && getTemplateName(selectedTemplate.sequence)}
              </Typography>
              <IconButton
                sx={{
                  svg: {
                    fill: "#1c1c1c"
                  }
                }}
                onClick={handleCloseModal}
              >
                <Close />
              </IconButton>
            </Box>
            <Box
              width={"100%"}
              sx={{ padding: { sm: 0, md: "10px 25px" } }}
              display={"flex"}
              gap="10px"
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Box width={"100%"}>
                <Suspense fallback={<div>Loading...</div>}>
                  <RichTextEditor
                    defaultValue={selectedTemplate?.content}
                    value={value}
                    setValue={setValue}
                    isLargeEditorNeeded={true}
                  />
                </Suspense>
              </Box>
            </Box>
            <Box sx={{ mt: 2, mb: 2 }}>
              <Button
                onClick={handleSubmit}
                disabled={!value}
                sx={{
                  textTransform: "none",
                  color: "#fcfcfc",
                  padding: "12px 20.5px",
                  background: "#1976d2",
                  fontWeight: 700,
                  "&:disabled": {
                    backgroundColor: "#A7C8E9",
                    color: "#FCFCFC",
                    pointerEvents: "none"
                  }
                }}
              >
                <SaveIcon sx={{ mr: 1 }} />
                Save Template
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal> */}
    </Container>
  );
};

export default AutomatedEmails;
